<template>
  <v-container
    id="reports-user"
    fluid
    tag="section"
    class="dashboard-container"
    style="padding: 24px 96px;"
  >
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <div class="d-flex align-center">
          <v-icon color="#00E5FF" large class="mr-3">mdi-file-chart-outline</v-icon>
          <h1 class="text-h5 font-weight-bold text--primary">Meus Relatórios</h1>
        </div>
      </v-col>
    </v-row>

    <v-card class="elevment-card mb-4 pa-4 elevation-0">
      <v-row align="center">
        <v-col cols="12" md="8" lg="8">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Buscar relatório"
            outlined
            dense
            hide-details
            clearable
            color="#00E5FF"
            class="elevment-input"
          />
        </v-col>

        <v-col cols="12" sm="6" md="2" lg="2">
          <v-select
            v-model="selectedPeriod"
            :items="periodOptions"
            :label="selectedPeriodLabel"
            prepend-inner-icon="mdi-calendar"
            outlined
            dense
            hide-details
            clearable
            color="#00E5FF"
            class="elevment-input"
          />
        </v-col>

        <v-col cols="12" sm="6" md="2" lg="2">
          <v-select
            v-model="selectedTypes"
            :items="fileTypeOptions"
            label="Todos os formatos"
            prepend-inner-icon="mdi-file-document-outline"
            outlined
            dense
            hide-details
            multiple
            clearable
            color="#00E5FF"
            class="elevment-input"
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0" class="text-caption">
                {{ selectedTypes.length === fileTypeOptions.length ? 'Todos formatos' : selectedTypes.length === 0 ? 'Todos...' : `${selectedTypes.length} selecionado${selectedTypes.length > 1 ? 's' : ''}` }}
              </span>
            </template>
            <template v-slot:prepend-item>
              <v-list-item @click="toggleAllFormats" class="all-formats-item">
                <v-list-item-action>
                  <v-icon :color="allFormatsSelected ? '#00E5FF' : ''">
                    {{ allFormatsSelected ? 'mdi-check' : 'mdi-checkbox-blank-outline' }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">Todos formatos</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-action>
                  <v-checkbox :input-value="attrs.inputValue" color="#00E5FF" hide-details />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-card>

    <v-alert
      type="info"
      dense
      text
      color="#00E5FF"
      icon="mdi-account-check-outline"
      class="mb-4 font-weight-medium"
      style="border-radius: 8px; background-color: rgba(0, 229, 255, 0.05) !important;"
    >
      Os relatórios são gerados com base nos seus próprios dados.
    </v-alert>

    <v-chip-group
      v-model="selectedCategory"
      mandatory
      class="mb-4"
    >
      <v-chip
        v-for="category in categoriesWithAll"
        :key="category.value"
        :value="category.value"
        outlined
        :class="[
          'font-weight-bold category-filter-chip',
          selectedCategory === category.value ? 'is-active' : 'is-inactive'
        ]"
      >
        <v-icon left small>{{ category.icon }}</v-icon>
        {{ category.label }}
        <v-chip
          v-if="category.count"
          x-small
          :class="[
            'ml-2 category-count-badge',
            selectedCategory === category.value ? 'is-active' : 'is-inactive'
          ]"
        >
          {{ category.count }}
        </v-chip>
      </v-chip>
    </v-chip-group>

    <v-row>
      <v-col
        v-for="report in filteredReports"
        :key="report.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card class="report-card elevment-card elevation-0" hover>
          <v-card-text>
            <div class="d-flex align-start">
              <v-avatar size="48" :color="report.iconColor" class="mr-3">
                <v-icon color="white">{{ report.icon }}</v-icon>
              </v-avatar>

              <div class="flex-grow-1">
                <h3 class="text-h6 font-weight-bold text--primary mb-1">{{ report.title }}</h3>
                <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium text-uppercase">
                  {{ report.category }}
                </div>
                <p class="text-body-2 text--secondary mb-3" style="line-height: 1.5">{{ report.description }}</p>

                <div class="mb-3 d-flex flex-wrap">
                  <v-chip
                    v-for="tag in report.tags"
                    :key="tag"
                    x-small
                    color="rgba(128,128,128,0.1)"
                    class="mr-1 mb-1 text--secondary text-uppercase"
                  >
                    {{ tag }}
                  </v-chip>
                </div>

                <div class="d-flex flex-wrap gap-2">
                  <v-btn
                    v-if="report.formats.includes('pdf') && isFormatVisible('pdf')"
                    x-small
                    outlined
                    color="#00E5FF"
                    class="font-weight-bold text-none"
                    :disabled="!!downloadingKey"
                    :loading="downloadingKey === `${report.id}-pdf`"
                    @click="downloadReport(report, 'pdf')"
                  >
                    <v-icon left x-small>mdi-download</v-icon>
                    PDF
                  </v-btn>
                  <v-btn
                    v-if="report.formats.includes('xlsx') && isFormatVisible('xlsx')"
                    x-small
                    outlined
                    color="#00E5FF"
                    class="font-weight-bold text-none"
                    :disabled="!!downloadingKey"
                    :loading="downloadingKey === `${report.id}-xlsx`"
                    @click="downloadReport(report, 'xlsx')"
                  >
                    <v-icon left x-small>mdi-download</v-icon>
                    XLSX
                  </v-btn>
                  <v-btn
                    v-if="report.formats.includes('csv') && isFormatVisible('csv')"
                    x-small
                    outlined
                    color="#00E5FF"
                    class="font-weight-bold text-none"
                    :disabled="!!downloadingKey"
                    :loading="downloadingKey === `${report.id}-csv`"
                    @click="downloadReport(report, 'csv')"
                  >
                    <v-icon left x-small>mdi-download</v-icon>
                    CSV
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="!loading && filteredReports.length === 0">
      <v-col cols="12" class="text-center py-12">
        <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-file-document-outline</v-icon>
        <h3 class="text-h6 mt-4 text--primary">Nenhum relatório encontrado</h3>
        <p class="text-body-2 text--secondary">Tente ajustar os filtros para ver mais resultados</p>
      </v-col>
    </v-row>

    <v-overlay :value="loading" absolute class="rounded-lg">
      <v-progress-circular indeterminate size="48" color="#00E5FF" width="4" />
    </v-overlay>
  </v-container>
</template>

<script>
import { ReportsService } from '@/services'

// Slugs e categorias de relatórios que são "por usuário"
const USER_SCOPED_SLUGS = [
  'adesao-pdis',
  'relatorio-one-on-ones',
  'relatorio-feedbacks',
]
const USER_SCOPED_CATEGORIES = [
  'engenharia',
]

export default {
  name: 'ReportsUser',

  data: () => ({
    loading: false,
    downloadingKey: null,
    searchQuery: '',
    selectedPeriod: null,
    selectedTypes: [],
    selectedCategory: null,

    reports: [],
    categories: [],

    periodOptions: [
      { text: 'Últimos 7 dias', value: 'last_7_days' },
      { text: 'Últimos 30 dias', value: 'last_30_days' },
      { text: 'Últimos 90 dias', value: 'last_90_days' },
      { text: 'Últimos 6 meses', value: 'last_6_months' },
      { text: 'Último ano', value: 'last_year' },
    ],

    fileTypeOptions: [
      { text: 'PDF', value: 'pdf' },
      { text: 'Excel (XLSX)', value: 'xlsx' },
      { text: 'CSV', value: 'csv' },
    ],
  }),

  computed: {
    currentUserId() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          return user.id || user._id || null
        }
      } catch {
        // noop
      }
      return null
    },

    allFormatsSelected() {
      return this.selectedTypes.length === this.fileTypeOptions.length
    },

    selectedPeriodLabel() {
      if (!this.selectedPeriod) return 'Período'
      const opt = this.periodOptions.find(o => o.value === this.selectedPeriod)
      return opt ? opt.text : 'Período'
    },

    // Apenas relatórios por usuário
    userScopedReports() {
      return this.reports.filter(r =>
        USER_SCOPED_SLUGS.includes(r.slug) ||
        USER_SCOPED_CATEGORIES.includes(r.category)
      )
    },

    categoriesWithAll() {
      let base = this.userScopedReports

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        base = base.filter(r =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.tags.some(tag => tag.toLowerCase().includes(q))
        )
      }

      const activeTypes = this.selectedTypes.filter(t => t)
      if (activeTypes.length > 0 && activeTypes.length < this.fileTypeOptions.length) {
        base = base.filter(r => activeTypes.some(type => r.formats.includes(type)))
      }

      const allCategory = { label: 'Todas', value: null, icon: 'mdi-file-document-outline', count: base.length }

      const withCounts = this.categories
        .filter(cat => base.some(r => r.category === cat.value))
        .map(cat => ({
          ...cat,
          count: base.filter(r => r.category === cat.value).length,
        }))

      return [allCategory, ...withCounts]
    },

    filteredReports() {
      let reports = this.userScopedReports.map(r => ({
        ...r,
        iconColor: r.icon_color,
      }))

      if (this.selectedCategory !== null) {
        reports = reports.filter(r => r.category === this.selectedCategory)
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        reports = reports.filter(r =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.tags.some(tag => tag.toLowerCase().includes(q))
        )
      }

      const activeTypes = this.selectedTypes.filter(t => t)
      if (activeTypes.length > 0 && activeTypes.length < this.fileTypeOptions.length) {
        reports = reports.filter(r =>
          activeTypes.some(type => r.formats.includes(type))
        )
      }

      return reports
    },
  },

  mounted() {
    this.loadReports()
  },

  methods: {
    async loadReports() {
      this.loading = true
      try {
        const response = await ReportsService.list()
        const allFormats = ['pdf', 'xlsx', 'csv']
        this.reports = (response.data.reports || []).map(r => ({
          ...r,
          formats: allFormats,
        }))
        this.categories = response.data.categories || []
      } catch (error) {
        console.error('Erro ao carregar relatórios:', error)
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'error',
          text: 'Erro ao carregar relatórios',
        })
      } finally {
        this.loading = false
      }
    },

    toggleAllFormats() {
      if (this.allFormatsSelected) {
        this.selectedTypes = []
      } else {
        this.selectedTypes = this.fileTypeOptions.map(opt => opt.value)
      }
    },

    isFormatVisible(format) {
      if (!this.selectedTypes.length || this.selectedTypes.length === this.fileTypeOptions.length) return true
      return this.selectedTypes.includes(format)
    },

    getTagColor(tag) {
      const colors = ['#FF9800', '#FF6B35', '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA']
      if (!tag) return colors[0]
      const index = tag.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length
      return colors[index]
    },

    async downloadReport(report, format) {
      if (this.downloadingKey) return

      this.downloadingKey = `${report.id}-${format}`
      this.$store.commit('SET_SNACKBAR', {
        show: true,
        color: 'info',
        text: `Gerando ${report.title} em formato ${format.toUpperCase()}...`,
      })

      try {
        const config = {
          format,
          period: this.selectedPeriod,
          filters: {},
        }

        // Sempre envia o userId do usuário logado
        if (this.currentUserId) {
          config.user_id = String(this.currentUserId)
        }

        console.log('🔹 Gerando relatório (user):', { reportId: report.id, slug: report.slug, config })

        await ReportsService.downloadReport(report.id, report.title, config)

        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'success',
          text: `✅ Download de ${report.title} concluído!`,
        })
      } catch (error) {
        console.error('Erro ao baixar relatório:', error)
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'error',
          text: 'Erro ao gerar relatório. Tente novamente.',
        })
      } finally {
        this.downloadingKey = null
      }
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* Base Cards */
.elevment-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #FFFFFF) !important;
}

/* Estilização dos Inputs Clean */
::v-deep .elevment-input .v-input__control {
  border-radius: 8px !important;
}

/* Report Cards */
.report-card {
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.report-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
}

.gap-2 {
  gap: 8px;
}

/* Chips e Tabs */
.category-filter-chip {
  border-radius: 999px !important;
  height: 34px !important;
  transition: all 0.2s ease;
}

.category-filter-chip .v-icon {
  color: inherit !important;
}

.category-filter-chip.is-inactive {
  background-color: #121417 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #A9AFB8 !important;
}

.category-filter-chip.is-active {
  background-color: #23D4F4 !important;
  border-color: #23D4F4 !important;
  color: #071117 !important;
}

.category-count-badge {
  min-width: 20px;
  height: 20px;
  border-radius: 999px !important;
  padding: 0 6px !important;
  font-weight: 700 !important;
  border: 1px solid transparent !important;
}

.category-count-badge.is-inactive {
  background-color: #1B1F24 !important;
  color: #8C929B !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.category-count-badge.is-active {
  background-color: #12BFD9 !important;
  color: #06232B !important;
}

/* Modo Dark Fallback para o Badge */
.theme--dark .category-filter-chip.is-inactive {
  background-color: #121417 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #A9AFB8 !important;
}

.theme--dark .category-filter-chip.is-active {
  background-color: #23D4F4 !important;
  border-color: #23D4F4 !important;
  color: #071117 !important;
}

/* Estilo para o item "Todos formatos" no dropdown */
.all-formats-item {
  background-color: rgba(0, 229, 255, 0.05) !important;
  transition: background-color 0.2s ease;
}

.all-formats-item:hover {
  background-color: rgba(0, 229, 255, 0.1) !important;
}

.all-formats-item .v-list-item__title {
  color: #00E5FF !important;
}
</style>